import { useState, useEffect, useMemo } from 'react';
import { coins } from '../../../utils/api';

// Field priority order
const FIELD_ORDER = [
  'certification_service_id',
  'certification_number',
  'pcgs_number',
  'cert_images',
  'country',
  'year',
  'mint_mark',
  'denomination',
  'variety',
  'purchase_date',
  'purchase_price'
];

// Hidden columns
const HIDDEN_COLUMNS = [
  'tag_id',
  'total_cost',
  'shipping_cost',
  'grading_cost'
];

export const useFormFields = (services, statuses) => {
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState(null);

  // Fetch columns only once when component mounts
  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const data = await coins.getColumns();
        setColumns(data);
      } catch (err) {
        console.error('Failed to fetch column metadata:', err);
        setError('Failed to load form fields');
      }
    };

    fetchColumns();
  }, []); // Empty dependency array - only fetch columns once

  // Memoize transformed fields
  const fields = useMemo(() => {
    if (!columns.length) return [];

    // Add custom fields
    const customFields = [{
      key: 'cert_images',
      label: 'Images',
      type: 'images',
      required: false,
      column_name: 'cert_images'
    }];

    // Transform database columns into fields
    const columnFields = columns
      .filter(col => !HIDDEN_COLUMNS.includes(col.column_name) && !col.hidden)
      .map(col => {
        const field = {
          key: col.column_name,
          label: col.column_name === 'certification_service_id' ? 'TPG' :
                 col.column_name === 'certification_number' ? 'Cert #' :
                 col.column_name === 'pcgs_number' ? 'PCGS #' :
                 col.friendly_name,
          required: col.required,
          type: 'text'
        };

        switch (col.data_type) {
          case 'integer':
          case 'numeric':
          case 'decimal':
            field.type = 'number';
            if (col.data_type !== 'integer') {
              field.step = '0.01';
            }
            break;
          case 'USER-DEFINED':
            if (col.column_name === 'cac_sticker') {
              field.type = 'select';
              field.options = [
                { value: 'none', label: 'None' },
                { value: 'green', label: 'Green' },
                { value: 'gold', label: 'Gold' }
              ];
            }
            break;
          case 'date':
            field.type = 'date';
            break;
          case 'timestamp':
            field.type = 'datetime-local';
            break;
          default:
            field.type = 'text';
        }

        if (col.column_name === 'certification_service_id') {
          field.type = 'select';
          field.options = [
            { value: '', label: 'None' },
            ...services.map(s => ({ value: s.id, label: s.name }))
          ];
        } else if (col.column_name === 'status_id') {
          field.type = 'select';
          field.options = [
            { value: '', label: 'None' },
            ...statuses.map(s => ({ value: s.id, label: s.name }))
          ];
          field.label = 'Status';
        }

        return field;
      });

    // Combine and sort all fields
    const allFields = [...customFields, ...columnFields].sort((a, b) => {
      const aIndex = FIELD_ORDER.indexOf(a.key);
      const bIndex = FIELD_ORDER.indexOf(b.key);
      
      // If both fields are in the priority list, sort by their order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one field is in priority list, it comes first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // For non-priority fields, sort alphabetically by label
      return a.label.localeCompare(b.label);
    });

    return allFields;
  }, [columns, services, statuses]);

  return {
    fields,
    error
  };
};
